import { useEffect, useState, useMemo } from "react"
import { listRuns } from "@/services/api"
import type { BackendRun } from "@/types"
import { Panel, SectionTitle } from "@/components/ui"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts"

export default function ComparisonPage() {
  const [runs, setRuns] = useState<BackendRun[]>([])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    listRuns()
      .then((res) => {
        if (!cancelled) setRuns(res.data)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-muted">
        <p>Loading runs&hellip;</p>
      </div>
    )
  }

  const selectedRuns = runs.filter((r) => selected.has(r.id))
  const canSelectMore = selectedRuns.length < 3

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else if (next.size < 3) next.add(id)
      return next
    })
  }

  const isSelected = (id: string) => selected.has(id)

  const comparison = useMemo(
    () =>
      selectedRuns.map((r, i) => {
        const m = r.metrics || {}
        return {
          id: r.id,
          label: `${r.algorithm.toUpperCase()} ${r.obs_mode ?? ""} seed=${r.seed ?? "?"}`.trim(),
          color: ["#22d3ee", "#3b82f6", "#f59e0b"][i % 3],
          landed: m.landing_success === true,
          touchdownVelocity: m.touchdown_velocity_mps ?? 0,
          horizontalError: m.horizontal_error_m ?? 0,
          fuelUsed: m.fuel_used_kg ?? 0,
          duration: m.duration_s ?? 0,
        }
      }),
    [selectedRuns],
  )

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-ink tracking-tight">Comparison</h1>
          <p className="text-muted text-sm">
            Compare up to 3 recorded runs by outcome and telemetry
          </p>
        </div>
        {selectedRuns.length > 0 && (
          <button
            onClick={() => setSelected(new Set())}
            className="px-3 py-1.5 rounded-sm text-xs font-medium bg-danger-500/10 text-danger-400 border border-danger-500/30 hover:bg-danger-500/20 transition"
          >
            Clear selection
          </button>
        )}
      </div>

      <SectionTitle>Run selection</SectionTitle>
      <Panel padded={false}>
        <div className="p-3 overflow-x-auto border-b border-border">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-border">
                <th className="px-3 py-2 text-[0.625rem] font-medium text-dim uppercase tracking-wider w-10">Select</th>
                <th className="px-3 py-2 text-[0.625rem] font-medium text-dim uppercase tracking-wider">Algorithm</th>
                <th className="px-3 py-2 text-[0.625rem] font-medium text-dim uppercase tracking-wider">Obs</th>
                <th className="px-3 py-2 text-[0.625rem] font-medium text-dim uppercase tracking-wider text-right">Status</th>
                <th className="px-3 py-2 text-[0.625rem] font-medium text-dim uppercase tracking-wider text-right">Touchdown</th>
                <th className="px-3 py-2 text-[0.625rem] font-medium text-dim uppercase tracking-wider text-right">Error</th>
                <th className="px-3 py-2 text-[0.625rem] font-medium text-dim uppercase tracking-wider text-right">Seed</th>
              </tr>
            </thead>
            <tbody>
              {runs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-muted text-sm">
                    No runs available. Run evaluations to populate history.
                  </td>
                </tr>
              ) : (
                runs.map((r) => {
                  const m = r.metrics || {}
                  const ok = m.landing_success === true
                  return (
                    <tr
                      key={r.id}
                      className={`border-b border-border/50 hover:bg-surface/50 transition group cursor-pointer ${isSelected(r.id) ? "bg-cyan-500/10 border-r-2 border-cyan-500" : ""}`}
                      onClick={() => toggle(r.id)}
                    >
                      <td className="px-3 py-2.5">
                        <input
                          type="checkbox"
                          checked={isSelected(r.id)}
                          onChange={() => toggle(r.id)}
                          disabled={!isSelected(r.id) && !canSelectMore}
                          className="rounded bg-surface border-border text-cyan-600 focus:ring-cyan-500"
                        />
                      </td>
                      <td className="px-3 py-2.5 text-sm text-ink">{r.algorithm.toUpperCase()}</td>
                      <td className="px-3 py-2.5 text-xs text-dim">{r.obs_mode ?? "—"}</td>
                      <td className="px-3 py-2.5 text-xs font-mono text-muted text-right">
                        {r.status}
                      </td>
                      <td className="px-3 py-2.5 text-xs font-mono text-ink text-right">
                        {ok ? m.touchdown_velocity_mps?.toFixed?.(1) ?? "—" : "—"} m/s
                      </td>
                      <td className="px-3 py-2.5 text-xs font-mono text-muted text-right">
                        {m.horizontal_error_m?.toFixed?.(1) ?? "—"} m
                      </td>
                      <td className="px-3 py-2.5 text-xs font-mono text-dim text-right">{r.seed ?? "—"}</td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      {selectedRuns.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <div className="lg:col-span-7 xl:col-span-8 gap-4">
            <Panel title="Outcome comparison" padded={false}>
              <div className="p-3 h-[300px]">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={comparison} margin={{ top: 8, right: 20, bottom: 8, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1c2533" />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 10, fill: "#5b6674" }}
                      axisLine={false}
                      tickMargin={8}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: "#5b6674" }}
                      axisLine={false}
                      tickMargin={8}
                    />
                    <Tooltip
                      contentStyle={{ backgroundColor: "#0b1018", border: "1px solid #232e3e", color: "#e8edf6", fontSize: 11, borderRadius: 2, padding: 6 }}
                    />
                    {comparison.map((c) => (
                      <Bar key={c.id} dataKey="landed" fill={c.color} radius={[2, 2, 0, 0]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-2 text-[0.625rem] text-muted">
                  Bar shows landed (1) vs impacted (0) for each selected run.
                </div>
              </div>
            </Panel>
          </div>

          <div className="lg:col-span-5 xl:col-span-4 gap-4">
            <Panel title="Metrics table" padded={false}>
              <div className="p-3">
                <table className="w-full text-sm text-left border-collapse">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="px-3 py-2 text-[0.625rem] font-medium text-dim uppercase tracking-wider">Metric</th>
                      {comparison.map((c) => (
                        <th key={c.id} className="px-3 py-2 text-[0.625rem] font-medium text-dim uppercase tracking-wider text-right">
                          {c.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border/50">
                      <td className="px-3 py-2 text-muted">Result</td>
                      {comparison.map((c) => (
                        <td key={c.id} className="px-3 py-2 font-mono text-ink text-right">
                          {c.landed ? "LANDED" : "IMPACTED"}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b border-border/50">
                      <td className="px-3 py-2 text-muted">Touchdown (m/s)</td>
                      {comparison.map((c) => (
                        <td key={c.id} className="px-3 py-2 font-mono text-ink text-right">{c.touchdownVelocity.toFixed(1)}</td>
                      ))}
                    </tr>
                    <tr className="border-b border-border/50">
                      <td className="px-3 py-2 text-muted">Horizontal error (m)</td>
                      {comparison.map((c) => (
                        <td key={c.id} className="px-3 py-2 font-mono text-muted text-right">{c.horizontalError.toFixed(1)}</td>
                      ))}
                    </tr>
                    <tr className="border-b border-border/50">
                      <td className="px-3 py-2 text-muted">Fuel used (kg)</td>
                      {comparison.map((c) => (
                        <td key={c.id} className="px-3 py-2 font-mono text-muted text-right">{c.fuelUsed.toFixed(1)}</td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>
        </div>
      )}
    </div>
  )
}
